import { apiClient } from './apiService';

export interface JiraConfig {
  baseUrl: string;
  email: string;
  apiToken: string;
}

export interface JiraConfigResponse {
  baseUrl: string;
  email: string;
  hasApiToken: boolean;
  isConfigured: boolean;
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  userInfo?: {
    displayName: string;
    emailAddress: string;
    accountId: string;
  };
}

export interface ValidationResult {
  success: boolean;
  message: string;
  errors?: string[];
}

export class JiraConfigService {
  /**
   * Get current Jira configuration (without sensitive data)
   */
  async getCurrentConfig(): Promise<{ success: boolean; data: JiraConfigResponse }> {
    try {
      const response = await apiClient.get('/jira-config');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get current configuration: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Update Jira configuration
   */
  async updateConfig(config: JiraConfig): Promise<{ success: boolean; message: string; updatedFields: string[] }> {
    try {
      const response = await apiClient.put('/jira-config', config);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to update configuration: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Test Jira connection with provided credentials
   */
  async testConnection(config: JiraConfig): Promise<ConnectionTestResult> {
    try {
      const response = await apiClient.post('/jira-config/test', config);
      return response.data;
    } catch (error: any) {
      // Return a proper ConnectionTestResult for network/API errors
      return {
        success: false,
        message: `Connection test failed: ${error.response?.data?.message || error.message}`
      };
    }
  }

  /**
   * Validate Jira configuration format without testing connection
   */
  async validateConfig(config: JiraConfig): Promise<ValidationResult> {
    try {
      const response = await apiClient.post('/jira-config/validate', config);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: 'Validation failed due to network error',
        errors: [error.message || 'Unknown network error occurred']
      };
    }
  }

  /**
   * Reset Jira configuration to empty state
   */
  async resetConfig(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/jira-config/reset');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to reset configuration: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Client-side validation helpers
   */
  validateUrl(url: string): { valid: boolean; message?: string } {
    if (!url) {
      return { valid: false, message: 'URL is required' };
    }

    try {
      const urlObj = new URL(url);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return { valid: false, message: 'URL must use http or https protocol' };
      }
      return { valid: true };
    } catch {
      return { valid: false, message: 'Invalid URL format' };
    }
  }

  validateEmail(email: string): { valid: boolean; message?: string } {
    if (!email) {
      return { valid: false, message: 'Email is required' };
    }

    // More comprehensive email validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (!emailRegex.test(email)) {
      return { valid: false, message: 'Please enter a valid email address' };
    }

    // Additional checks
    if (email.length > 254) {
      return { valid: false, message: 'Email address is too long' };
    }

    // Check for consecutive dots
    if (email.includes('..')) {
      return { valid: false, message: 'Email address cannot contain consecutive dots' };
    }

    return { valid: true };
  }

  validateApiToken(token: string): { valid: boolean; message?: string } {
    if (!token) {
      return { valid: false, message: 'API token is required' };
    }

    if (token.length < 10) {
      return { valid: false, message: 'API token appears to be too short' };
    }

    return { valid: true };
  }

  /**
   * Validate entire configuration
   */
  validateFullConfig(config: JiraConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    const urlValidation = this.validateUrl(config.baseUrl);
    if (!urlValidation.valid && urlValidation.message) {
      errors.push(urlValidation.message);
    }

    const emailValidation = this.validateEmail(config.email);
    if (!emailValidation.valid && emailValidation.message) {
      errors.push(emailValidation.message);
    }

    const tokenValidation = this.validateApiToken(config.apiToken);
    if (!tokenValidation.valid && tokenValidation.message) {
      errors.push(tokenValidation.message);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Format base URL to ensure it's clean
   * Note: Defaults to HTTPS for security, but preserves explicit HTTP if specified
   */
  formatBaseUrl(url: string): string {
    if (!url) return '';

    // Remove trailing slash
    let formatted = url.trim().replace(/\/$/, '');

    // Only add https if no protocol is specified at all
    // This preserves explicit http:// if the user intended it
    if (!formatted.startsWith('http://') && !formatted.startsWith('https://')) {
      // For Jira instances, HTTPS is strongly recommended
      formatted = `https://${formatted}`;
    }

    return formatted;
  }

  /**
   * Check if URL needs protocol and warn user
   */
  analyzeUrlProtocol(url: string): { needsProtocol: boolean; isHttp: boolean; warning?: string } {
    if (!url) return { needsProtocol: false, isHttp: false };

    const trimmed = url.trim();
    const needsProtocol = !trimmed.startsWith('http://') && !trimmed.startsWith('https://');
    const isHttp = trimmed.startsWith('http://');

    let warning: string | undefined;
    if (needsProtocol) {
      warning = 'Protocol not specified. HTTPS will be used for security.';
    } else if (isHttp) {
      warning = 'HTTP is not secure. Consider using HTTPS for production Jira instances.';
    }

    return { needsProtocol, isHttp, warning };
  }

  /**
   * Extract domain from Jira URL for display
   */
  extractDomain(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  }

  /**
   * Check if configuration appears to be for Atlassian Cloud
   */
  isAtlassianCloud(baseUrl: string): boolean {
    try {
      const urlObj = new URL(baseUrl);
      return urlObj.hostname.includes('atlassian.net');
    } catch {
      return false;
    }
  }

  /**
   * Generate help text for API token based on URL
   */
  getApiTokenHelpText(baseUrl: string): string {
    if (this.isAtlassianCloud(baseUrl)) {
      return 'For Atlassian Cloud, create an API token at: https://id.atlassian.com/manage-profile/security/api-tokens';
    } else {
      return 'For Jira Server/Data Center, use your password or create a personal access token';
    }
  }

  /**
   * Get configuration status for UI display
   */
  getConfigStatus(config: JiraConfigResponse): {
    status: 'complete' | 'partial' | 'empty';
    message: string;
    color: 'success' | 'warning' | 'error';
  } {
    if (config.isConfigured) {
      return {
        status: 'complete',
        message: 'Jira is fully configured and ready to use',
        color: 'success'
      };
    }

    if (config.baseUrl || config.email || config.hasApiToken) {
      return {
        status: 'partial',
        message: 'Jira configuration is incomplete',
        color: 'warning'
      };
    }

    return {
      status: 'empty',
      message: 'Jira is not configured',
      color: 'error'
    };
  }

  /**
   * Mask API token for display (shows minimal information for security)
   */
  maskApiToken(token: string): string {
    if (!token) return '';
    if (token.length <= 8) return '••••••••';

    // Show only the last 3 characters for security
    // This is enough to help users identify which token they're using
    // without revealing too much sensitive information
    const end = token.substring(token.length - 3);
    const masked = '•'.repeat(Math.max(8, token.length - 3));

    return `${masked}${end}`;
  }

  /**
   * Get connection test status for UI
   */
  getTestStatus(result: ConnectionTestResult): {
    color: 'success' | 'error';
    icon: string;
    title: string;
  } {
    if (result.success) {
      return {
        color: 'success',
        icon: '✅',
        title: 'Connection Successful'
      };
    } else {
      return {
        color: 'error',
        icon: '❌',
        title: 'Connection Failed'
      };
    }
  }
}

// Create singleton instance
export const jiraConfigService = new JiraConfigService();
